import React from 'react';
import './sharedComponent.css';

/* ── Button ──────────────────────────────────────────────────── */
export const Button = ({
  children, variant = 'primary', size = 'md',
  loading = false, disabled = false, icon, className = '', ...props
}) => (
  <button
    className={`btn btn--${variant} btn--${size} ${loading ? 'btn--loading' : ''} ${className}`}
    disabled={disabled || loading}
    {...props}
  >
    {loading ? <span className="btn__spinner" /> : icon && <span className="btn__icon">{icon}</span>}
    {children}
  </button>
);

/* ── Input ───────────────────────────────────────────────────── */
export const Input = React.forwardRef(({
  label, error, hint, required, className = '', ...props
}, ref) => (
  <div className={`field ${error ? 'field--error' : ''} ${className}`}>
    {label && (
      <label className="field__label">
        {label}{required && <span className="field__required">*</span>}
      </label>
    )}
    <input ref={ref} className="field__input" {...props} />
    {hint && !error && <span className="field__hint">{hint}</span>}
    {error && <span className="field__error">{error}</span>}
  </div>
));
Input.displayName = 'Input';

/* ── Select ──────────────────────────────────────────────────── */
export const Select = React.forwardRef(({
  label, error, required, children, className = '', ...props
}, ref) => (
  <div className={`field ${error ? 'field--error' : ''} ${className}`}>
    {label && (
      <label className="field__label">
        {label}{required && <span className="field__required">*</span>}
      </label>
    )}
    <select ref={ref} className="field__input field__select" {...props}>
      {children}
    </select>
    {error && <span className="field__error">{error}</span>}
  </div>
));
Select.displayName = 'Select';

/* ── Textarea ────────────────────────────────────────────────── */
export const Textarea = React.forwardRef(({
  label, error, required, rows = 3, className = '', ...props
}, ref) => (
  <div className={`field ${error ? 'field--error' : ''} ${className}`}>
    {label && (
      <label className="field__label">
        {label}{required && <span className="field__required">*</span>}
      </label>
    )}
    <textarea ref={ref} rows={rows} className="field__input field__textarea" {...props} />
    {error && <span className="field__error">{error}</span>}
  </div>
));
Textarea.displayName = 'Textarea';

/* ── Badge / Status ──────────────────────────────────────────── */
const STATUS_CONFIG = {
  draft:       { label: 'Draft',       dot: '#94A3B8' },
  sent:        { label: 'Submitted',   dot: '#0284C7' },
  in_progress: { label: 'In Progress', dot: '#D97706' },
  submitted:   { label: 'Completed',   dot: '#16A34A' },
  overdue:     { label: 'Pending',     dot: '#DC2626' },
  closed:      { label: 'Closed',      dot: '#64748B' },
};

// The "sent" status (dispatched, not yet started) reads as "Submitted" to
// internal staff but as "Received" to the data provider it was sent to.
export const StatusBadge = ({ status, context }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, dot: '#94A3B8' };
  const label = status === 'sent' && context === 'external' ? 'Received' : cfg.label;
  return (
    <span className={`badge badge--${status}`}>
      <span className="badge__dot" style={{ background: cfg.dot }} />
      {label}
    </span>
  );
};

/* ── Period badges (quarters/months) ────────────────────────────
   Renders each selected period (e.g. "Q1 2026") as its own small chip
   instead of one long comma-separated string. */
export const PeriodBadges = ({ periods, fallback = '—' }) => {
  const list = (periods || []).filter(Boolean);
  if (!list.length) return <span className="period-badges__empty">{fallback}</span>;
  return (
    <span className="period-badges">
      {list.map((p) => <span key={p} className="period-badge">{p}</span>)}
    </span>
  );
};

/* ── Compact number (for large totals) ──────────────────────────
   Abbreviates figures at or above `threshold` (e.g. 12500000 → "12.5M")
   for readability; the full-precision value is always available via
   the title tooltip so nothing is actually lost. Below the threshold
   it renders the full number, unchanged. */
export const CompactNumber = ({ value, decimals = 2, threshold = 1_000_000 }) => {
  const num = parseFloat(value);
  if (!isFinite(num)) return <>—</>;
  const full = num.toLocaleString('en-NA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  if (Math.abs(num) < threshold) return <>{full}</>;
  const compact = new Intl.NumberFormat('en-NA', { notation: 'compact', maximumFractionDigits: 1 }).format(num);
  return <span title={full}>{compact}</span>;
};

/* ── Spinner ─────────────────────────────────────────────────── */
export const Spinner = ({ size = 'md', className = '' }) => (
  <span className={`spinner spinner--${size} ${className}`} aria-label="Loading..." />
);

/* ── Card ────────────────────────────────────────────────────── */
export const Card = ({ children, className = '', padding = true, ...props }) => (
  <div className={`card ${padding ? 'card--padded' : ''} ${className}`} {...props}>
    {children}
  </div>
);

/* ── Modal ───────────────────────────────────────────────────── */
export const Modal = ({ open, onClose, title, children, size = 'md', footer }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal modal--${size}`} role="dialog" aria-modal="true">
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          <button className="modal__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

/* ── Empty State ─────────────────────────────────────────────── */
export const EmptyState = ({ icon, title, description, action }) => (
  <div className="empty-state">
    {icon && <div className="empty-state__icon">{icon}</div>}
    <h4 className="empty-state__title">{title}</h4>
    {description && <p className="empty-state__desc">{description}</p>}
    {action && <div className="empty-state__action">{action}</div>}
  </div>
);

/* ── Page Header ─────────────────────────────────────────────── */
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="page-header">
    <div className="page-header__text">
      <h1 className="page-header__title">{title}</h1>
      {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="page-header__actions">{actions}</div>}
  </div>
);